import React, {useState, useEffect, useContext, useRef, useCallback} from 'react';
import { UserContext, WorkspaceContext } from '../../objects/Context';
import './DataSources.css';
import Row from '../../components/Row/Row';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import {ReactComponent as AddIcon} from '../../assets/icons/add-noFill-icon.svg'
import {ReactComponent as DataIcon} from '../../assets/icons/data-icon.svg'
import {ReactComponent as RefreshIcon} from '../../assets/icons/refresh-icon.svg'
import {ReactComponent as SandboxIcon} from '../../assets/icons/info-icon-noFill.svg';

import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Popup from '../../components/Popup/Popup';
import Alert from '../../components/Alerts/Alert';
import DataSourceDetails from './DataSourceDetails';
import CreateDataSource from './CreateDataSource';
import UsagePopup from '../../components/ErrorHandler/UsagePopup';
import ErrorHandler from '../../components/ErrorHandler/ErrorHandler';

const SYNC_STATUS_POLL_INTERVAL_MS = 60000;

const sortDataSources = (sources) => {
    return [...sources].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

function DataSources() {
    const { user } = useContext(UserContext);
    const { workspace } = useContext(WorkspaceContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [dataSources, setDataSources] = useState([]);
    const [dataSourcesLoaded, setDataSourcesLoaded] = useState(false);
    const [selectedView, setSelectedView] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Create Data Source
    const [addDataSourceOpen, setAddDataSourceOpen] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSubmessage, setAlertSubmessage] = useState('');
    const [alertSuccess, setAlertSuccess] = useState(true);
    const [alertTimeout, setAlertTimeout] = useState(5000);
    // Data Source Details
    const [dataSourceDetailsOpen, setDataSourceDetailsOpen] = useState(false);
    const [selectedDataSource, setSelectedDataSource] = useState(null);
    const [showUsagePopup, setShowUsagePopup] = useState(false);
    // sandbox workspace
    const [isSandboxHovered, setIsSandboxHovered] = useState(false);
    const [masterLoading, setMasterLoading] = useState(false);
    const pollingInFlightRef = useRef(false);

    ///////////////////// useEffects /////////////////////
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = '';
        };
    }, []);

    const fetchDataSources = useCallback(async ({ silent = false } = {}) => {
        if (!user?.id || !workspace?.id) return [];

        if (!silent) {
            setError('');
        }

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-data-sources/${user.id}/${workspace.id}`, { withCredentials: true });

            // Add check for "Vairo Pipeline" and sort by creation date
            const modifiedDataSources = sortDataSources(response.data);
            setDataSources(modifiedDataSources);
            return modifiedDataSources;
        } catch (error) {
            if (!silent) {
                setError(error.code);
            } else {
                console.error('Error refreshing data source sync status:', error);
            }
            return [];
        } finally {
            setDataSourcesLoaded(true);
            if (!silent) {
                setLoading(false);
            }
        }
    }, [user?.id, workspace?.id]);

    useEffect(() => {
        fetchDataSources();
    }, [fetchDataSources]);

    useEffect(() => {  
        if (dataSources.length > 0 && loading) {
            //fetchUsersWithAccess();
        } else if (dataSourcesLoaded && loading) {
            setLoading(false);
        }
    }, [dataSources]);

    useEffect(() => {
        setSelectedDataSource(dataSources.find(dataSource => dataSource.id === selectedDataSource?.id));
    }, [dataSources]);

    useEffect(() => {
        const hasSyncingDataSource = dataSources.some(dataSource => dataSource.status === 'syncing');
        if (!hasSyncingDataSource) return;

        const refreshSyncingDataSources = async () => {
            if (pollingInFlightRef.current) return;

            pollingInFlightRef.current = true;
            try {
                await fetchDataSources({ silent: true });
            } finally {
                pollingInFlightRef.current = false;
            }
        };

        const intervalId = setInterval(refreshSyncingDataSources, SYNC_STATUS_POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [dataSources, fetchDataSources]);

    // Check for navigation state to open add data source modal
    useEffect(() => {
        if (location.state?.addDataSourceOpen) {
            setAddDataSourceOpen(true);
            // Clear the state to prevent reopening on subsequent renders
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);


    ///////////////////// Database Functions /////////////////////

    const handleDeleteDataSource = async (dataSourceId) => {
        const dataSourceToDelete = dataSources.find(dataSource => dataSource.id === dataSourceId);
        if (!dataSourceToDelete) return;
        setDataSources((prevDataSources) => prevDataSources.filter((dataSource) => dataSource.id !== dataSourceId));
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/remove-data-source`, {
                id: dataSourceId,
                vectorFileId: dataSourceToDelete.vectorFileId,
                workspaceId: workspace.id,
                vectorStoreId: workspace.vectorStoreId,
                databaseName: dataSourceToDelete?.databaseName,
                schemaName: dataSourceToDelete?.schemaName,
                sourceId: dataSourceToDelete?.sourceId,
                destinationId: dataSourceToDelete?.destinationId,
                connectionId: dataSourceToDelete?.connectionId
            }, { withCredentials: true });
        } catch (error) {
            console.error('Error deleting data source:', error);
            setDataSources((prevDataSources) => [
                ...prevDataSources,
                dataSourceToDelete
            ]);
        }
    };

    const updateDataSource = async (dataSourceId, updates) => {
        setDataSources((prevDataSources) =>
            prevDataSources.map((dataSource) =>
                dataSource.id === dataSourceId ? { ...dataSource, ...updates } : dataSource
            )
        );
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/update-data-source`, {
                dataSourceId,
                userId: user.id,
                workspaceId: workspace.id,
                updates
            }, { withCredentials: true });
        } catch (error) {
            console.error('Error updating data source:', error);
        }
    }

    const disconnectDataSource = async ({dataSourceId, dataSourceType}) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/disconnect`, {
                dataSourceId,
                workspaceId: workspace.id,
                vectorStoreId: workspace.vectorStoreId,
                dataSourceType
            }, { withCredentials: true });
            setDataSources((prevDataSources) => prevDataSources.map((dataSource) => 
                dataSource.id === dataSourceId 
                    ? { ...dataSource, status: 'disconnected', lastSynced: null }
                    : dataSource
            ));
        } catch (error) {
            console.error('Error disconnecting data source:', error);
            setAlertMessage('Error disconnecting data source');
            setAlertSubmessage('An unexpected error occurred. Please try again later.');
            setAlertSuccess(false);
            setAlertOpen(true);
        }
    };

    const createSandboxWorkspace = async () => {
        if (user?.orgRole !== 'admin') {
            setAlertMessage('You are not authorized to create a workspace.');
            setAlertSubmessage('Please contact your administrator to create a workspace.');
            setAlertSuccess(false);
            setAlertOpen(true);
            return;
        }

        setMasterLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/workspaces/create-sandbox`, {
                userId: user.id,
                orgId: user.orgId
            }, { withCredentials: true });
            switchWorkspace({workspaceId: response.data.id});
        } catch (err) {
            console.error('Error creating sandbox workspace:', err);
            if (err?.response?.status === 403) {
                setAlertMessage('You are not authorized to create a workspace.');
                setAlertSubmessage('Please contact your administrator to create a workspace.');
            } else {
                setAlertMessage('There was an Issue Creating Your Sandbox.');
                setAlertSubmessage('Vairo only supports one sandbox workspace per organization. Please reach out to us if you\'d like to increase this limit!');
            }
            setAlertSuccess(false);
            setAlertOpen(true);
        } finally {
            setMasterLoading(false);
        }
    }

    const switchWorkspace = async ({workspaceId}) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/switch-workspace/${user.id}/${workspaceId}`, { withCredentials: true });
            window.location.href = '/';
        } catch (error) {
            console.error('Switch workspace failed:', error);
        }
    };

    ///////////////////// handler functions /////////////////////
    const handleEditDataSource = (dataSource) => {
        setDataSourceDetailsOpen(true);
        setSelectedDataSource(dataSource);
    };

    const getFilteredDataSources = () => {
        switch (selectedView) {
            case 'my':
                return dataSources.filter(dataSource => dataSource.creatorUserId === user.id);
            case 'shared':
                return dataSources.filter(dataSource => dataSource.creatorUserId !== user.id);
            default: // 'all'
                return dataSources;
        }
    };

    const setAlert = (message, submessage, success, timeout) => {
        setAlertMessage(message);
        setAlertSubmessage(submessage);
        setAlertSuccess(success);
        setAlertTimeout(timeout);
        setAlertOpen(true);
    };

    const handleRefresh = () => {
        const refreshButton = document.querySelector('.header-action.refresh');
        refreshButton.classList.add('spinning');
        setDataSourcesLoaded(false);
        setLoading(true);
        setDataSources([]);
        fetchDataSources();
        setTimeout(() => {
            refreshButton.classList.remove('spinning');
        }, 800);
    };

    const handleAddDataSource = () => {
        setAddDataSourceOpen(true);
    }

    ///////////////////// render functions /////////////////////
    const renderAddSourceModal = () => (
        <Popup
            content={<CreateDataSource setAddDataSourceOpen={setAddDataSourceOpen} setDataSources={setDataSources} updateDataSource={updateDataSource} setAlert={setAlert} setShowUsagePopup={setShowUsagePopup} />}
            onClose={() => setAddDataSourceOpen(false)}
        >
        </Popup>
    );

    const renderDataSourceDetails = () => (
        <Popup
            content={<DataSourceDetails dataSource={selectedDataSource} setDataSources={setDataSources} updateDataSource={updateDataSource} setAlert={setAlert} setModelOpen={setDataSourceDetailsOpen} disconnectDataSource={disconnectDataSource} />}
            onClose={() => setDataSourceDetailsOpen(false)}
        >
        </Popup>
    );

    const renderMasterLoading = () => (
        <Popup
            content={
                <div className="master-loading">
                    <LoadingScreen message="Setting up sandbox..." />
                </div> 
            }
            onClose={() => {}}
        />
    );

    return (
        <div className="wrapper data-sources-wrapper">
            {loading ? (
                <LoadingScreen />
            ) : (
                error ? (
                    <ErrorHandler errorCode={error} />
                ) : (
                    <div className="data-sources-content">
                        {addDataSourceOpen && renderAddSourceModal()}
                        {masterLoading && renderMasterLoading()}
                        <div className='detail-header'>
                            <h2>Integrations</h2>
                            <div className='detail-header-actions'>
                                <div onClick={handleRefresh} className='header-action refresh'>
                                    <RefreshIcon className={'icon refresh'} />
                                </div>
                                <div onClick={() => handleAddDataSource()} className='header-action add'>
                                    <AddIcon className={'icon add'} />
                                    <span>Add Source</span>
                                </div>
                            </div>
                        </div>
                        <div className="dashboards-sub-header">
                            <h4 className={selectedView === 'all' ? 'active' : ''} onClick={() => setSelectedView('all')}>All</h4>
                            <h4 className={selectedView === 'my' ? 'active' : ''} onClick={() => setSelectedView('my')}>My Integrations</h4>
                            <h4 className={selectedView === 'shared' ? 'active' : ''} onClick={() => setSelectedView('shared')}>Shared with me</h4>
                            <div className="selection-line"></div>
                        </div>
                        {getFilteredDataSources().length > 0 &&
                            <div className="data-sources-table-container">
                                <table className="data-sources-table">
                                    <thead>
                                        <tr>
                                            <th>Connection Name</th>
                                            <th>Data Source</th>
                                            <th>Date Created</th>
                                            <th>Role</th>
                                            <th>Access</th>
                                            <th>Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {getFilteredDataSources().map(ds => (
                                        <Row 
                                            key={ds.id} 
                                            dataSourceData={ds} 
                                            userId={user.id}
                                            handleEditDataSource={handleEditDataSource}
                                            handleDeleteDataSource={handleDeleteDataSource}
                                        />
                                    ))}
                                    </tbody>
                                </table>
                                {dataSourceDetailsOpen && renderDataSourceDetails()}
                            </div>
                        }
                        {getFilteredDataSources().length === 0 &&
                            <div className="no-data-sources">
                                <DataIcon className="no-data-icon" />
                                <p>No integrations found.</p>
                                { selectedView === 'shared' ? (
                                    <p className="sub-text">No integrations have been shared with you yet.</p>
                                ) : (
                                    <>
                                        <p className="sub-text">Add an integration to get started!</p>
                                        <button className="add-data-source-button" onClick={() => handleAddDataSource()}>
                                            <AddIcon className={'icon add'} />
                                            <span>Add Source</span>
                                        </button>
                                        <div className="divider">- or -</div>
                                        <div
                                            className="action sandbox"
                                            onClick={() => { createSandboxWorkspace(); }}
                                            onMouseEnter={() => setIsSandboxHovered(true)}
                                            onMouseLeave={() => setIsSandboxHovered(false)}
                                            style={{ position: 'relative' }}
                                        >
                                            {isSandboxHovered && (
                                                <div className="sandbox-tooltip">
                                                    Our <strong>sandbox workspace</strong> is a temporary environment where you can try out Vairo using a demo QuickBooks account that's already set up.
                                                </div>
                                            )}
                                            <span>Try Sandbox QuickBooks</span>
                                            <SandboxIcon className='icon' />
                                        </div>
                                    </>
                                )}
                            </div>
                        }
                        {/* {alertOpen && <Alert message={"Data Source Syncing"} submessage={"You can begin using this data source once the sync is complete."} success={true} onClose={() => setAlertOpen(false)} />} */}
                        {alertOpen && <Alert message={alertMessage} submessage={alertSubmessage} success={alertSuccess} timeout={alertTimeout} onClose={() => setAlertOpen(false)} />}
                        {showUsagePopup && <UsagePopup message={"Data Source Limit Reached"} submessage={"You have reached your limit. Please upgrade your plan to add more data sources!"} onClose={() => setShowUsagePopup(false)} />}
                    </div>
                )
            )}
        </div>
    );
};

export default DataSources;